import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Zap, FileText, BookMarked } from 'lucide-react';
interface BonusProps {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ElementType;
  spineColor: string;
  delay: number;
}
function BonusCard({
  number,
  title,
  subtitle,
  description,
  icon: Icon,
  spineColor,
  delay
}: BonusProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6,
    delay
  }} className="bg-ed-dark border border-zinc-800 rounded-3xl p-8 sm:p-10 relative overflow-hidden group hover:border-ed-green/40 transition-all" data-id="element-21">
      {/* Glow */}
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-ed-green/10 blur-3xl rounded-full pointer-events-none group-hover:bg-ed-green/20 transition-colors" data-id="element-22"></div>

      <div className="relative z-10 grid sm:grid-cols-[1fr_auto] gap-8 items-center" data-id="element-23">
        {/* Copy */}
        <div className="order-2 sm:order-1" data-id="element-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ed-yellow/10 border border-ed-yellow/30 mb-4" data-id="element-25">
            <Gift className="w-3.5 h-3.5 text-ed-yellow" data-id="element-26" />
            <span className="text-xs font-black tracking-widest text-ed-yellow uppercase" data-id="element-27">
              Bonus {number}
            </span>
          </div>

          <p className="text-zinc-500 uppercase text-xs font-bold tracking-widest mb-2" data-id="element-28">
            {subtitle}
          </p>
          <h3 className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight mb-4" data-id="element-29">
            {title}
          </h3>
          <p className="text-zinc-400 leading-relaxed" data-id="element-30">{description}</p>
        </div>

        {/* Book Mockup */}
        <div className="order-1 sm:order-2 mx-auto" data-id="element-31">
          <div className="w-40 sm:w-44 aspect-[1/1.4] perspective-1000" data-id="element-32">
            <div className="w-full h-full relative preserve-3d transform rotate-[-8deg] group-hover:rotate-0 transition-transform duration-700" data-id="element-33">
              <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 to-ed-darker rounded-r-md rounded-l-sm border border-zinc-700/50 shadow-2xl overflow-hidden" data-id="element-34">
                {number === '1' || number === '2' ? <>
                    <img src={number === '1' ? 'https://cdn.magicpatterns.com/uploads/kNcbivAJdum6gmBrHY6YqW/PORTADA_MODULO_4_WEB.png' : 'https://cdn.magicpatterns.com/uploads/3jFjotxZ9iY3y3fC5pUKnn/RESUMEN_DE_NORMATIVA_WEB.png'} alt={`Bonus ${number} — ${title}`} className="absolute inset-0 w-full h-full object-cover object-top" data-id="element-35" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-30 pointer-events-none" data-id="element-36"></div>
                  </> : <>
                    {/* Spine */}
                    <div className={`absolute left-0 top-0 bottom-0 w-2.5 border-r border-black/20 z-10 ${spineColor}`} data-id="element-37"></div>

                    {/* Cover content */}
                    <div className="absolute inset-0 pl-5 p-4 flex flex-col justify-between" data-id="element-38">
                      <div className="flex justify-between items-start" data-id="element-39">
                        <span className="text-ed-yellow font-black text-xs tracking-widest uppercase" data-id="element-40">
                          Bonus
                        </span>
                        <Zap className="w-4 h-4 text-zinc-600" data-id="element-41" />
                      </div>

                      <div className="flex items-center justify-center flex-grow" data-id="element-42">
                        <Icon className="w-12 h-12 text-ed-green" strokeWidth={1.5} data-id="element-43" />
                      </div>

                      <div data-id="element-44">
                        <div className="text-[9px] text-zinc-500 uppercase tracking-widest font-semibold mb-1" data-id="element-45">
                          Material Exclusivo
                        </div>
                        <h4 className="text-white font-black text-sm leading-tight uppercase" data-id="element-46">
                          {title}
                        </h4>
                      </div>
                    </div>

                    {/* Gloss */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-60 pointer-events-none" data-id="element-47"></div>
                  </>}
              </div>

              {/* Pages edge */}
              <div className="absolute top-1 bottom-1 -right-1.5 w-1.5 bg-zinc-300 rounded-r-sm" data-id="element-48"></div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function Bonuses() {
  return <section id="bonus" className="py-24 bg-ed-darker relative border-t border-white/5" data-id="element-49">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-50">
        <div className="text-center mb-14 max-w-2xl mx-auto" data-id="element-51">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-ed-yellow/10 border border-ed-yellow/30 mb-5" data-id="element-52">
            <Gift className="w-4 h-4 text-ed-yellow" data-id="element-53" />
            <span className="text-xs font-bold tracking-widest text-ed-yellow uppercase" data-id="element-54">
              Material extra incluido
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight text-white mb-4" data-id="element-55">
            Bonos <span className="text-ed-yellow" data-id="element-56">exclusivos</span>
          </h2>
          <p className="text-zinc-400 text-lg" data-id="element-57">
            Recursos prácticos adicionales para acelerar tu transformación
            profesional desde el primer día.
          </p>
        </div>

        <div className="grid gap-8" data-id="element-58">
          <BonusCard number="1" subtitle="Módulo 4 - Material complementario" title="Guía de Circuitos de Electricidad Domiciliaria" description="Circuitos reales explicados paso a paso para comprender instalaciones prácticas. Una herramienta visual indispensable para resolver casos concretos en cualquier vivienda." icon={FileText} spineColor="bg-ed-yellow" delay={0.1} data-id="element-59" />
          <BonusCard number="2" subtitle="Resumen aplicado" title="Resumen Práctico de Normativa AEA" description="Resumen simplificado y práctico de normativa vigente aplicada a instalaciones reales. Lo esencial que tenés que saber sin tecnicismos innecesarios." icon={BookMarked} spineColor="bg-ed-green" delay={0.2} data-id="element-60" />
        </div>
      </div>
    </section>;
}